import React, { useEffect, useState } from "react";
import { Modal, Table, ConfigProvider } from "antd";
import Loader from "../../utils/loader";
import {
  fetchAddendumPdfUrl,
  fetchAgreementPdfUrl,
  formatDate,
  formatRoute,
  getOperatorName,
} from "../../utils/agreementMapping";
import styles from "./agreementTracking.module.scss";

const rupee = (value) =>
  value !== undefined && value !== null && value !== "" ? `₹${value}` : null;

const EFFECTIVE_FROM_FIELD = {
  label: "Effective From",
  value: (d) => formatDate(d.effectiveFromDate),
};
const EFFECTIVE_TILL_FIELD = {
  label: "Effective Till",
  value: (d) => formatDate(d.effectiveTillDate),
};

const OEM_LABELS = { NAARNI_AZAD: "Naarni/Azad", JBM: "JBM" };
const APPROVAL_STATUS_LABELS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

const MODEL_TYPE_LABELS = {
  MG_AG: "MG/AG",
  REVENUE_SHARING: "Revenue Sharing",
};
const CONTRACT_TYPE_LABELS = {
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY_RECON: "Monthly Recon",
};

const percent = (value) =>
  value !== undefined && value !== null && value !== "" ? `${value}%` : null;

const AGREEMENT_NAME_FIELD = {
  label: "Agreement Name",
  value: (d, item) => item.agreement.name,
};
const APPROVAL_STATUS_FIELD = {
  label: "Operator Approval Status",
  value: (d) => APPROVAL_STATUS_LABELS[d.operatorActionStatus],
};

const routeCities = (d) =>
  d.fromCity && d.toCity ? `${d.fromCity.name} - ${d.toCity.name}` : null;

// Fields shown per contract type, mirroring each contract's view screen in
// Zupiter. Every field is listed; missing values show as "-". `value` gets the
// raw contract and the normalized item (for agreement-level fields).
const CONTRACT_FIELDS = {
  CONTRACT: [
    { label: "Contract Name", value: (d) => d.name },
    { label: "Operator Name", value: getOperatorName },
    {
      label: "Model Type",
      value: (d) => MODEL_TYPE_LABELS[d.modelType] || d.modelType,
    },
    {
      label: "Contract Type",
      value: (d) => CONTRACT_TYPE_LABELS[d.type] || d.type,
    },
    { label: "Contract Status", value: (d) => d.status },
    EFFECTIVE_FROM_FIELD,
    EFFECTIVE_TILL_FIELD,
    { label: "Route", value: routeCities },
    { label: "Bus Layout", value: (d) => d.busLayout },
    { label: "Number of Seats", value: (d) => d.numberOfSeats },
    { label: "Route Kms", value: (d) => d.routeKms },
    { label: "Running Cost", value: (d) => rupee(d.runningCost) },
    {
      label: "Bus Transformation Cost",
      value: (d) => rupee(d.busTransformationCost),
    },
    {
      label: "Intra Connecting Max Supply",
      value: (d) => d.intraConnectingMaxSupply,
    },
    { label: "Operator GST Rate", value: (d) => percent(d.operatorGstRate) },
    { label: "Cancellation Charge", value: (d) => rupee(d.cancellationCharge) },
    { label: "Free Demand Cancellations", value: (d) => d.freeDemandCancellation },
    { label: "Base fuel rate", value: (d) => rupee(d.baseFuelRate) },
    { label: "Round Trip Toll", value: (d) => rupee(d.roundTripToll) },
    { label: "Additional Guarantee", value: (d) => d.additionalGuarantee },
    { label: "Deficit Cover", value: (d) => rupee(d.deficitCover) },
    {
      label: "Revenue Split (zingbus% - Operator%)",
      value: (d) =>
        d.revenueSplit
          ? `${d.revenueSplit.zingbusShare}-${d.revenueSplit.operatorShare}`
          : null,
    },
  ],
  EV_SERVICE_CONTRACT: [
    { label: "Contract Name", value: (d) => d.name },
    { label: "Electric Operator Name", value: getOperatorName },
    AGREEMENT_NAME_FIELD,
    APPROVAL_STATUS_FIELD,
    EFFECTIVE_FROM_FIELD,
    EFFECTIVE_TILL_FIELD,
    { label: "Route", value: (d) => d.route },
    { label: "Route Cities", value: routeCities },
    { label: "Bus Layout", value: (d) => d.busLayout },
    { label: "Single Side Route Km", value: (d) => d.singleSideRouteKm },
    {
      label: "Single Side Deficit Cover",
      value: (d) => rupee(d.singleSideDeficitCover),
    },
    {
      label: "Commission",
      value: (d) => percent(d.commissionPercentage),
    },
    {
      label: "Intra Connecting Max Supply",
      value: (d) => d.intraConnectingMaxSupply,
    },
    { label: "Contract Status", value: (d) => d.status },
  ],
  EV_LEASE_CONTRACT: [
    { label: "Contract Name", value: (d) => d.name },
    AGREEMENT_NAME_FIELD,
    {
      label: "Agreement Operator",
      value: (d, item) => item.agreement.operatorName,
    },
    APPROVAL_STATUS_FIELD,
    { label: "Version", value: (d) => d.version },
    { label: "RC", value: (d) => d.rc },
    { label: "OEM Name", value: (d) => OEM_LABELS[d.oem] || d.oem },
    { label: "Bus Layout", value: (d) => d.busLayout },
    { label: "Start Date", value: (d) => formatDate(d.startDate) },
    { label: "End Date", value: (d) => formatDate(d.endDate) },
    { label: "Lease Per Month", value: (d) => rupee(d.leasePerMonth) },
    { label: "Monthly Km", value: (d) => d.monthlyKm },
    { label: "Hand Over Km", value: (d) => d.handOverKm },
    { label: "Lease Pause Days", value: (d) => d.leasePauseDays },
    {
      label: "Assured Days Of Operations",
      value: (d) => d.assuredDaysOfOperations,
    },
    { label: "Lock In Period", value: (d) => d.lockInPeriod },
    { label: "Deposit", value: (d) => rupee(d.deposit) },
    {
      label: "Notice Duration (Months)",
      value: (d) => d.noticeDurationInMonths,
    },
    { label: "Cancellation Charge", value: (d) => rupee(d.cancellationCharge) },
    { label: "Contract Status", value: (d) => d.status },
  ],
};

const REVENUE_RULE_COLUMNS = [
  { title: "A (Round Trip Fixed MG)", dataIndex: "A" },
  { title: "B (Operator Share)", dataIndex: "B" },
  { title: "C (zingbus share)", dataIndex: "C" },
  { title: "D (Revenue sharing)", dataIndex: "D" },
  { title: "Sharing Ratio (zingbus% - Operator %)", dataIndex: "sharing" },
];

// Same bracket formatting as the Service Contracts page.
const revenueRuleRows = (revenueBracket) => {
  const bracketRange = (bracket) =>
    bracket
      ? bracket.start + (bracket.end ? " to " + bracket.end : "")
      : null;
  return [
    {
      key: "rules",
      A: revenueBracket[0] ? revenueBracket[0].end : null,
      B: bracketRange(revenueBracket[1]),
      C: bracketRange(revenueBracket[2]),
      D: revenueBracket[3] ? "Above " + revenueBracket[3].start : null,
      sharing:
        revenueBracket[3] &&
        revenueBracket[3].zingbusShare &&
        revenueBracket[3].operatorShare
          ? revenueBracket[3].zingbusShare +
            "-" +
            revenueBracket[3].operatorShare
          : null,
    },
  ];
};

const ContractDetails = ({ item }) => {
  const details = item.details;
  const fields = (CONTRACT_FIELDS[item.entityType] || []).map((field) => {
    const value = field.value(details, item);
    return {
      label: field.label,
      value: value === undefined || value === null || value === "" ? "-" : value,
    };
  });
  const revenueBracket = details.revenueBracket;

  return (
    <>
      <div className={styles.contractDetails}>
        {fields.map((field) => (
          <div key={field.label}>
            <span>{field.label}</span>
            <span>{field.value}</span>
          </div>
        ))}
      </div>
      {Array.isArray(revenueBracket) && revenueBracket.length === 4 ? (
        <div className={styles.revenueRules}>
          <p>Revenue Rules :</p>
          <ConfigProvider theme={{ token: { colorPrimary: "#7b2bff" } }}>
            <Table
              columns={REVENUE_RULE_COLUMNS}
              dataSource={revenueRuleRows(revenueBracket)}
              bordered
              pagination={false}
            />
          </ConfigProvider>
        </div>
      ) : null}
    </>
  );
};

const PdfViewer = ({ url, loading, emptyText }) => {
  if (loading) {
    return (
      <div className={styles.pdfViewer}>
        <div className={styles.placeholder}>
          <Loader />
        </div>
      </div>
    );
  }
  return (
    <div className={styles.pdfViewer}>
      {url ? (
        <>
          <iframe src={url} title="PDF Viewer" />
          <a href={url} target="_blank" rel="noreferrer" download>
            Download
          </a>
        </>
      ) : (
        <div className={styles.placeholder}>{emptyText}</div>
      )}
    </div>
  );
};

// Fetches a short-lived PDF URL when the modal opens.
const RemotePdf = ({ fetchUrl, id, emptyText }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchUrl(id).then((pdfUrl) => {
      if (cancelled) return;
      setUrl(pdfUrl);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchUrl, id]);

  return <PdfViewer url={url} loading={loading} emptyText={emptyText} />;
};

// `target` is either { agreement } or { item }.
const DetailsModal = ({ target, onClose }) => {
  if (!target) return null;
  const { agreement, item } = target;

  let title;
  let subtitle;
  let body;
  let width = 622;
  if (agreement) {
    title = "Agreement Details";
    body = (
      <RemotePdf
        fetchUrl={fetchAgreementPdfUrl}
        id={agreement.id}
        emptyText="Agreement document is not available"
      />
    );
  } else if (item.entityType === "ADDENDUM") {
    title = "Addendum Details";
    body = (
      <RemotePdf
        fetchUrl={fetchAddendumPdfUrl}
        id={item.id}
        emptyText="Addendum PDF is not available"
      />
    );
  } else {
    title = "Contract Details";
    subtitle = formatRoute(item.details);
    body = <ContractDetails item={item} />;
    width = 1150;
  }

  return (
    <Modal
      open
      centered
      width={width}
      footer={null}
      onCancel={onClose}
      destroyOnClose
      title={
        <span className={styles.modalTitle}>
          <span>{title}</span>
          {subtitle ? <span>{subtitle}</span> : null}
        </span>
      }
    >
      {body}
    </Modal>
  );
};

export default DetailsModal;
